import { useState, type ReactElement, type ReactNode } from 'react'

import { useSympla } from './api/sympla-api'
import { CalendarioEventos } from './components/calendario-eventos'
import { CurvaVendas } from './components/curva-vendas'
import { rotuloCategoriaVip, rotuloMeioPagamento, rotuloStatusPedido } from './lib/formata-sympla'

import type { RelatorioSympla } from './types'

import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { FunilVisual } from '@/features/pre-vendas/components/funil-visual'
import { cn } from '@/lib/cn'
import { inteiro, moedaCompacta, percentual } from '@/lib/formato'

function pct(valor: number | null): string {
  return valor === null ? '—' : percentual(valor / 100)
}

function CartaoTelao({
  titulo,
  children,
  className,
}: {
  titulo: string
  children: ReactNode
  className?: string
}): ReactElement {
  return (
    <div
      className={cn('bg-card/70 border-primary/15 flex flex-col rounded-2xl border p-4', className)}
    >
      <h3 className="mb-3 text-2xl font-semibold">{titulo}</h3>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  )
}

interface ColunaTelao {
  chave: string
  rotulo: string
  alinharDireita?: boolean
}

interface LinhaTelao {
  chave: string
  celulas: Record<string, ReactNode>
}

/** Tabela em escala de telão: mesmo contrato da TabelaCompacta, tipografia maior. */
function TabelaTelao({
  colunas,
  linhas,
}: {
  colunas: ColunaTelao[]
  linhas: LinhaTelao[]
}): ReactElement {
  return (
    <table className="w-full text-xl">
      <thead>
        <tr className="border-border border-b">
          {colunas.map((coluna) => (
            <th
              key={coluna.chave}
              className={cn(
                'text-muted-foreground px-2 py-2 text-base font-semibold uppercase tracking-wide',
                coluna.alinharDireita ? 'text-right' : 'text-left',
              )}
            >
              {coluna.rotulo}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {linhas.map((linha) => (
          <tr key={linha.chave} className="border-border/60 border-b last:border-0">
            {colunas.map((coluna) => (
              <td
                key={coluna.chave}
                className={cn('px-2 py-2', coluna.alinharDireita && 'text-right tabular-nums')}
              >
                {linha.celulas[coluna.chave]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function PendenciaTelao({ texto }: { texto: string }): ReactElement {
  return <p className="text-muted-foreground text-xl">{texto}</p>
}

function TabelaAgenda({
  eventos,
}: {
  eventos: NonNullable<RelatorioSympla['agenda']>
}): ReactElement {
  return (
    <TabelaTelao
      colunas={[
        { chave: 'dia', rotulo: 'Data' },
        { chave: 'evento', rotulo: 'Evento' },
        { chave: 'local', rotulo: 'Local' },
      ]}
      linhas={eventos.map((e) => ({
        chave: `${e.dia}-${e.evento}`,
        celulas: {
          dia: diaCurto(e.dia),
          evento: e.evento,
          local: e.local ?? '—',
        },
      }))}
    />
  )
}

const AGENDA_POR_PAGINA = 12

function AbaAgenda({
  agenda,
  pendencia,
}: {
  agenda: RelatorioSympla['agenda']
  pendencia: string | null
}): ReactElement {
  const [pagina, setPagina] = useState(1)

  if (agenda === null) {
    return (
      <div className="grid flex-1 gap-3">
        <CartaoTelao titulo="Agenda — calendário do Hub, por data">
          <PendenciaTelao texto={pendencia ?? 'Member Hub desligado.'} />
        </CartaoTelao>
      </div>
    )
  }
  if (agenda.length === 0) {
    return (
      <div className="grid flex-1 gap-3">
        <CartaoTelao titulo="Agenda — calendário do Hub, por data">
          <PendenciaTelao texto="Nenhum evento futuro no calendário do Hub." />
        </CartaoTelao>
      </div>
    )
  }

  const totalPaginas = Math.max(1, Math.ceil(agenda.length / AGENDA_POR_PAGINA))
  const paginaAtual = Math.min(pagina, totalPaginas)
  const visiveis = agenda.slice(
    (paginaAtual - 1) * AGENDA_POR_PAGINA,
    paginaAtual * AGENDA_POR_PAGINA,
  )

  return (
    <div className="grid flex-1 grid-cols-[1.6fr_1fr] gap-3">
      <CartaoTelao titulo="Calendário">
        <CalendarioEventos eventos={agenda} telao />
      </CartaoTelao>
      <CartaoTelao titulo="Agenda — calendário do Hub, por data">
        <div className="flex h-full flex-col">
          <div className="flex-1">
            <TabelaAgenda eventos={visiveis} />
          </div>
          {totalPaginas > 1 ? (
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-muted-foreground text-lg">
                Página {paginaAtual} de {totalPaginas}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPagina(paginaAtual - 1)}
                  disabled={paginaAtual <= 1}
                  className="border-border hover:bg-muted rounded-lg border px-5 py-2 text-lg disabled:opacity-40"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => setPagina(paginaAtual + 1)}
                  disabled={paginaAtual >= totalPaginas}
                  className="border-border hover:bg-muted rounded-lg border px-5 py-2 text-lg disabled:opacity-40"
                >
                  Próxima
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </CartaoTelao>
    </div>
  )
}

// Telas internas do slide: a agenda de próximos eventos e o BI do IMR26.
const ABAS_TELA = [
  { tela: 'agenda', rotulo: 'Próximos eventos' },
  { tela: 'evento', rotulo: 'IMR26 — vendas' },
  { tela: 'pipeline', rotulo: 'IMR26 — pipeline & cupons' },
] as const

type TelaSympla = (typeof ABAS_TELA)[number]['tela']

/** 'YYYY-MM-DD' → 'DD/MM' sem passar por Date — date-only não tem fuso. */
function diaCurto(dia: string): string {
  return dia.slice(8) + '/' + dia.slice(5, 7)
}

/**
 * O slide "Próximos Eventos" em escala de telão (canvas 2500×1400 escalado
 * pelo PalcoFit): a agenda do Hub por data, e o BI do IMR26 — o evento em
 * destaque — nas outras duas abas (vendas; funil do CRM e cupons).
 */
export function BlocoSymplaDenso(): ReactElement {
  const [tela, setTela] = useState<TelaSympla>('agenda')
  const { data: resultado, isLoading, isError } = useSympla()
  const relatorio = resultado?.relatorio ?? null

  if (isError) {
    return <EmptyState title="BI do IMR26 indisponível" description="A coleta no Sympla falhou." />
  }
  if (isLoading || !resultado) {
    return <Skeleton className="h-64" />
  }
  if (!resultado.configurado || !relatorio) {
    return (
      <EmptyState
        title="Evento do Sympla não configurado"
        description={resultado.pendencia ?? 'Configure o Sympla para ativar o painel.'}
      />
    )
  }

  const k = relatorio.kpis
  const v = relatorio.vips
  const p = relatorio.pipeline
  const kpis: { rotulo: string; valor: string; detalhe?: string }[] = [
    {
      rotulo: 'Ingressos válidos',
      valor: inteiro(k.ingressosValidos),
      detalhe: `${inteiro(k.pedidosAprovados)} pedido(s)`,
    },
    {
      rotulo: 'Receita bruta',
      valor: moedaCompacta(k.receitaBruta),
      detalhe: `Líquida: ${moedaCompacta(k.receitaLiquida)}`,
    },
    {
      rotulo: 'Ticket médio pagante',
      valor: k.ticketMedioPedido === null ? '—' : moedaCompacta(k.ticketMedioPedido),
      detalhe: `${inteiro(k.pedidosPagantes)} pagante(s) · ${inteiro(k.cortesias)} cortesia(s)`,
    },
    {
      rotulo: 'Check-ins',
      valor: inteiro(k.checkIns),
      detalhe: `Taxa: ${pct(k.taxaCheckIn)}`,
    },
    {
      rotulo: 'VIPs (Hub)',
      valor: v === null ? '—' : inteiro(v.inscritos),
      detalhe: v === null ? 'Hub desligado' : `${inteiro(v.checkIns)} check-in(s)`,
    },
    {
      rotulo: 'Público total',
      valor: v === null ? '—' : inteiro(v.publicoTotal),
      detalhe: v === null ? undefined : `${inteiro(v.compraramSympla)} repetido(s)`,
    },
    {
      rotulo: 'Vendas CRM',
      valor: p === null ? '—' : inteiro(p.ganhos),
      detalhe: p === null ? 'CRM desligado' : `${inteiro(p.perdidos)} perdida(s)`,
    },
    {
      rotulo: 'Dias p/ evento',
      valor: relatorio.evento.diasAteEvento > 0 ? inteiro(relatorio.evento.diasAteEvento) : '🎉',
      detalhe: relatorio.evento.cidadeUf ?? undefined,
    },
  ]

  return (
    // Altura MÍNIMA, não fixa: tabela alta cresce o canvas e o PalcoFit
    // re-escala pra caber — com altura fixa ela vazava do slide.
    <div className="flex min-h-[1400px] w-full flex-col gap-3 p-1">
      <div className="flex items-start gap-3">
        <div className="bg-primary h-14 w-2 shrink-0 rounded" aria-hidden />
        <div>
          <h1 className="font-serif text-5xl font-normal leading-none tracking-tight">
            Próximos Eventos
          </h1>
          <p className="text-muted-foreground mt-2 text-xl">
            Em destaque: {relatorio.evento.nome}
          </p>
        </div>
        <span className="text-primary ml-auto self-center font-serif text-3xl tracking-[0.35em]">
          ENJOY BUSINESS
        </span>
      </div>

      <div className="grid shrink-0 grid-cols-8 gap-3">
        {kpis.map((kpi) => (
          <div
            key={kpi.rotulo}
            className="bg-card/70 border-primary/15 flex flex-col justify-center rounded-xl border px-4 py-4"
          >
            <p className="text-muted-foreground text-sm font-semibold uppercase tracking-wide">
              {kpi.rotulo}
            </p>
            <p className="text-primary mt-1 font-serif text-4xl leading-none">{kpi.valor}</p>
            {kpi.detalhe ? (
              <p className="text-muted-foreground mt-1.5 text-base">{kpi.detalhe}</p>
            ) : null}
          </div>
        ))}
      </div>

      <div className="flex shrink-0 gap-3">
        {ABAS_TELA.map((aba) => (
          <button
            key={aba.tela}
            type="button"
            onClick={() => setTela(aba.tela)}
            aria-pressed={tela === aba.tela}
            className={cn(
              'rounded-lg border px-6 py-3 text-xl font-medium transition-colors',
              tela === aba.tela
                ? 'border-primary bg-primary/15 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {aba.rotulo}
          </button>
        ))}
      </div>

      {tela === 'agenda' ? (
        <AbaAgenda agenda={relatorio.agenda} pendencia={resultado.pendenciaCruzamento} />
      ) : tela === 'evento' ? (
        <>
          <div className="grid flex-1 grid-cols-[1.6fr_1fr] gap-3">
            <CartaoTelao titulo="Curva de vendas">
              <CurvaVendas curva={relatorio.curvaVendas} telao />
            </CartaoTelao>
            <CartaoTelao titulo="Lotes">
              <TabelaTelao
                colunas={[
                  { chave: 'lote', rotulo: 'Lote' },
                  { chave: 'ingressos', rotulo: 'Ingressos', alinharDireita: true },
                  { chave: 'receita', rotulo: 'Receita', alinharDireita: true },
                  { chave: 'preco', rotulo: 'Preço médio', alinharDireita: true },
                ]}
                linhas={relatorio.lotes.map((l) => ({
                  chave: l.lote,
                  celulas: {
                    lote: l.lote,
                    ingressos: inteiro(l.ingressos),
                    receita: moedaCompacta(l.receita),
                    preco: moedaCompacta(l.precoMedio),
                  },
                }))}
              />
            </CartaoTelao>
          </div>

          <div className="grid flex-1 grid-cols-3 gap-3">
            <CartaoTelao titulo="Canais (UTM)">
              <TabelaTelao
                colunas={[
                  { chave: 'canal', rotulo: 'Canal' },
                  { chave: 'pedidos', rotulo: 'Pedidos', alinharDireita: true },
                  { chave: 'receita', rotulo: 'Receita', alinharDireita: true },
                ]}
                linhas={relatorio.canais.map((c) => ({
                  chave: c.canal,
                  celulas: {
                    canal: c.canal,
                    pedidos: inteiro(c.pedidos),
                    receita: moedaCompacta(c.receita),
                  },
                }))}
              />
            </CartaoTelao>
            <CartaoTelao titulo="Pagamento e status">
              <TabelaTelao
                colunas={[
                  { chave: 'rotulo', rotulo: 'Meio / status' },
                  { chave: 'qtd', rotulo: 'Pedidos', alinharDireita: true },
                ]}
                linhas={[
                  ...relatorio.meiosPagamento.map((m) => ({
                    chave: `meio-${m.meio}`,
                    celulas: { rotulo: rotuloMeioPagamento(m.meio), qtd: inteiro(m.pedidos) },
                  })),
                  ...relatorio.statusPedidos.map((s) => ({
                    chave: `status-${s.status}`,
                    celulas: { rotulo: rotuloStatusPedido(s.status), qtd: inteiro(s.qtd) },
                  })),
                ]}
              />
            </CartaoTelao>
            <CartaoTelao titulo="VIPs por categoria">
              {v === null ? (
                <PendenciaTelao
                  texto={resultado.pendenciaCruzamento ?? 'Member Hub desligado.'}
                />
              ) : (
                <TabelaTelao
                  colunas={[
                    { chave: 'categoria', rotulo: 'Categoria' },
                    { chave: 'participantes', rotulo: 'Inscritos', alinharDireita: true },
                  ]}
                  linhas={v.porCategoria.map((c) => ({
                    chave: c.categoria,
                    celulas: {
                      categoria: rotuloCategoriaVip(c.categoria),
                      participantes: inteiro(c.participantes),
                    },
                  }))}
                />
              )}
            </CartaoTelao>
          </div>
        </>
      ) : (
        <>
          <div className="grid flex-1 grid-cols-[1fr_1.2fr] gap-3">
            <CartaoTelao titulo={p ? `Funil — ${p.nome}` : 'Funil do CRM'}>
              {p === null ? (
                <PendenciaTelao texto={resultado.pendenciaPipeline ?? 'CRM desligado.'} />
              ) : (
                <FunilVisual
                  passos={p.funil.map((f) => ({
                    rotulo: f.etapa,
                    qtd: f.qtd,
                    pctDoAnterior: null,
                  }))}
                  telao
                />
              )}
            </CartaoTelao>
            <CartaoTelao titulo="Vendas por executivo">
              {p === null ? (
                <PendenciaTelao texto="As vendas por executivo vêm do pipeline do evento." />
              ) : (
                <>
                  <TabelaTelao
                    colunas={[
                      { chave: 'executivo', rotulo: 'Executivo' },
                      { chave: 'vendas', rotulo: 'Vendas', alinharDireita: true },
                      { chave: 'valor', rotulo: 'Valor', alinharDireita: true },
                    ]}
                    linhas={p.vendasPorExecutivo.map((e) => ({
                      chave: e.executivo,
                      celulas: {
                        executivo: e.executivo,
                        vendas: inteiro(e.vendas),
                        valor: moedaCompacta(e.valor),
                      },
                    }))}
                  />
                  <p className="text-muted-foreground mt-3 text-xl">
                    {inteiro(p.ganhos)} venda(s) ganha(s) · {moedaCompacta(p.valorGanho)} ·{' '}
                    {inteiro(p.perdidos)} perdida(s)
                  </p>
                </>
              )}
            </CartaoTelao>
          </div>

          <div className="grid flex-1 gap-3">
            <CartaoTelao titulo="Inscritos por cupom">
              <TabelaTelao
                colunas={[
                  { chave: 'cupom', rotulo: 'Cupom' },
                  { chave: 'pedidos', rotulo: 'Pedidos', alinharDireita: true },
                  { chave: 'ingressos', rotulo: 'Ingressos', alinharDireita: true },
                  { chave: 'receita', rotulo: 'Receita', alinharDireita: true },
                ]}
                linhas={relatorio.cupons.map((c) => ({
                  chave: c.cupom,
                  celulas: {
                    cupom: c.cupom,
                    pedidos: inteiro(c.pedidos),
                    ingressos: inteiro(c.ingressos),
                    receita: moedaCompacta(c.receita),
                  },
                }))}
              />
            </CartaoTelao>
          </div>
        </>
      )}
    </div>
  )
}
